import axios from "axios";

/**
 * Адрес страницы входа спрашиваем у бэкенда (Ф1 ТЗ единой авторизации).
 *
 * Раньше фронт собирал его сам из переменной сборки, и это стреляло: стенд, на котором
 * забыли задать VITE_AUTH_ENTRY_URL, отправлял людей вводить пароль в ПРОД. Сборка
 * раздаётся всем окружениям одинаковой, поэтому решать, какому контуру принадлежит
 * сессия, обязан сервер — он один знает свой контур.
 *
 * Там же, на сервере, живёт и проверка пути возврата: `return_to` уезжает в адрес REG,
 * и без проверки вход становится способом увести человека на чужой домен.
 *
 * Запрос идёт «голым» axios, а не общим перехватчиком, и на то две причины. Ручка
 * публичная и ничего из его работы не требует — ни заголовка защиты, ни разбора 401.
 * А главное, перехватчик на 401 сам уводит человека сюда же: спрашивать у него адрес
 * входа означало бы замкнуть импорты в кольцо, а поведение — в петлю.
 */

const ENTRY_URL = "/api/auth/unified/entry";

const RECOVER_URL = "/api/auth/unified/recover";

/** Заголовок, которым сообщаем серверу, куда вернуть человека после входа. */
const RETURN_TO_HEADER = "X-Return-To";

interface AuthEntryResponse {
  status: boolean;
  data?: { login_url?: string | null };
}

interface RecoverResponse {
  status: boolean;
  data?: { recovered?: boolean };
}

/**
 * Адрес страницы входа или null, если уводить некуда.
 *
 * @param leaving true — человек уходит прямо сейчас (сервер засчитает попытку);
 *                false — спрашиваем только чтобы показать домен рядом с кнопкой.
 *
 * Null — не ошибка связи, а ответ по существу: либо адрес не настроен на сервере, либо
 * это уже вторая попытка подряд и сработала анти-петля. В обоих случаях фронт обязан
 * показать экран «войти не удалось», а не уходить наугад.
 */
export async function fetchLoginUrl(returnTo: string, leaving = false): Promise<string | null> {
  try {
    const { data } = await axios.get<AuthEntryResponse>(ENTRY_URL, {
      headers: { [RETURN_TO_HEADER]: returnTo },
      // Намерение уйти отделено от вопроса «какой домен показать рядом с кнопкой»:
      // серверная анти-петля считает попыткой только первое, иначе она срабатывала
      // бы на обычном входе (см. EplexAuthController::entry)
      params: leaving ? { leaving: 1 } : undefined,
    });

    const loginUrl = data?.data?.login_url;

    return typeof loginUrl === "string" && loginUrl.length > 0 ? loginUrl : null;
  } catch (error) {
    // Недоступность бэкенда — это не повод угадывать адрес входа. Человек увидит
    // экран «войти не удалось», и это честнее ухода на чужой контур.
    console.error("Единая авторизация: не удалось получить адрес страницы входа", error);

    return null;
  }
}

/**
 * Жива ли сессия, от которой в браузере остался один refresh (CAB-690).
 *
 * Зачем это нужно. Кука eplex_token живёт ровно столько же, сколько токен внутри неё,
 * — тридцать минут, — и по истечении браузер удаляет её сам. Человеку, у которого
 * кабинет просто открыт, запросов делать неоткуда, поэтому продлевать пару некому:
 * он возвращается, обновляет страницу и попадает на вход, хотя refresh годен ещё
 * четверо суток. Гард видит только отсутствие куки и различить эти два состояния не
 * может — спрашиваем сервер, он видит refresh.
 *
 * Сам факт восстановления делает бэкенд до аутентификации, на любом запросе; здесь
 * лишь явная точка спроса для страницы входа.
 */
export async function recoverSession(): Promise<boolean> {
  try {
    const { data } = await axios.get<RecoverResponse>(RECOVER_URL);

    return data?.data?.recovered === true;
  } catch {
    // 401 здесь — штатный ответ «восстанавливать нечего», а не сбой: молчим и уводим
    // человека на вход обычным путём
    return false;
  }
}
